import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  Calendar,
  MapPin,
  Plane,
  Users,
  Phone,
  DollarSign,
  CheckCircle2,
} from "lucide-react";
import dayjs from "dayjs";
import { get, ref } from "firebase/database";

import { database } from "../firebase/firebase";
import LoadingScreen from "../utils/LoadingScreen";
import logo from "../assets/images/logo.png";
import { Button } from "../components/ui/button";
import { whatsappUrl } from "../config/contact";

const currencySymbol = (currency: string) => {
  switch (currency) {
    case "dollar":
      return "$";
    case "euro":
      return "€";
    case "pesos":
      return "₱";
    default:
      return "R$";
  }
};

export default function Voucher() {
  const [booking, setBooking] = useState<any>();
  const [passengersName, setPassengersName] = useState<string[]>([]);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    get(ref(database, `booking/${id}`)).then((snapshot) => {
      if (snapshot.exists()) {
        setBooking(snapshot.val());
      } else {
        navigate("/");
      }
    });
  }, [id, navigate]);

  useEffect(() => {
    if (!booking) return;
    if (booking.passengersQty >= 2) {
      const passengers = [booking.name];
      for (let i = 2; i <= booking.passengersQty; i++) {
        if (booking[`passenger${i}`]) {
          passengers.push(booking[`passenger${i}`]);
        }
      }
      setPassengersName(passengers);
    } else {
      setPassengersName([booking.name]);
    }
  }, [booking]);

  if (!booking) {
    return <LoadingScreen />;
  }

  const openGoogleMaps = () => {
    const url = `https://www.google.com/maps/search/?q=${encodeURIComponent(
      booking.destination
    )}`;
    window.open(url, "_blank");
  };

  const isAppleDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipod|ipad|macintosh/.test(userAgent);
  };

  const formatDate = (date: Date) => {
    const adjusted = new Date(date.getTime() - 3 * 60 * 60 * 1000);
    const yyyy = adjusted.getUTCFullYear();
    const mm = String(adjusted.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(adjusted.getUTCDate()).padStart(2, "0");
    const hh = String(adjusted.getUTCHours()).padStart(2, "0");
    const min = String(adjusted.getUTCMinutes()).padStart(2, "0");
    const sec = String(adjusted.getUTCSeconds()).padStart(2, "0");
    return `${yyyy}${mm}${dd}T${hh}${min}${sec}Z`;
  };

  const createAppleCalendarEvent = () => {
    const title = `Transfer ${booking.origin} para ${booking.destination}`;
    const details = `Viagem de ${booking.origin} para ${booking.destination}, ${booking.passengersQty} passageiros.`;
    const location = booking.destination;
    const startDate = new Date(booking.date);
    const endDate = new Date(booking.date);
    endDate.setHours(endDate.getHours() + 1);
    const startDateFormatted = formatDate(startDate);
    const endDateFormatted = formatDate(endDate);
    const icsContent = `BEGIN:VCALENDAR\nBEGIN:VEVENT\nSUMMARY:${title}\nDESCRIPTION:${details}\nLOCATION:${location}\nDTSTART;TZID=America/Sao_Paulo:${startDateFormatted}\nDTEND;TZID=America/Sao_Paulo:${endDateFormatted}\nSTATUS:CONFIRMED\nSEQUENCE:1\nURL:https://www.mbfloripaturismo.com.br/voucher/${id}\nBEGIN:VALARM\nTRIGGER:-PT1H\nDESCRIPTION:Lembrete\nACTION:DISPLAY\nEND:VALARM\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transfer.ics`;
    link.click();
  };

  const createGoogleCalendarEvent = () => {
    const title = `Transfer ${booking.origin} para ${booking.destination}`;
    const details = `Viagem de ${booking.origin} para ${booking.destination}, ${booking.passengersQty} passageiros.`;
    const location = booking.destination;
    const startDate = new Date(booking.date);
    const endDate = new Date(booking.date);
    endDate.setHours(endDate.getHours() + 1);
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      title
    )}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(
      location
    )}&dates=${startDate.toISOString().replace(/-|:|\.\d+/g, "")}/${endDate
      .toISOString()
      .replace(/-|:|\.\d+/g, "")}`;
    window.open(url, "_blank");
  };

  const handleOpenCheckin = () => {
    window.open(`https://mbfloripaturismo.com.br/checkin/${id}`, "_blank");
  };

  const symbol = currencySymbol(booking.currency || "real");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 py-8 md:py-12">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <Link to="/" className="inline-block">
            <img
              className="mx-auto h-12 w-auto"
              src={logo}
              alt="MB Floripa Turismo"
            />
          </Link>
        </div>

        {/* Ticket Card */}
        <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-900/5">
          {/* Confirmation Badge */}
          <div className="bg-gradient-to-r from-primary to-blue-600 px-6 py-4 text-center">
            <div className="flex items-center justify-center gap-2 text-white">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-bold uppercase tracking-widest">
                Reserva Confirmada
              </span>
            </div>
          </div>

          {/* Ticket Content */}
          <div className="p-6 md:p-8">
            {/* Passenger Name */}
            <div className="mb-6 border-b border-slate-100 pb-6">
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {booking.name}
              </h1>
              <p className="mt-2 text-sm text-slate-500 font-medium">
                Sua viagem está confirmada e aguardamos você!
              </p>
            </div>

            {/* Route Section */}
            <div className="mb-8 rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-900/5">
              <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
                <div className="flex-1 text-center md:text-left">
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">
                    Origem
                  </p>
                  <p className="mt-1 text-lg font-bold text-slate-900 leading-tight">
                    {booking.origin}
                  </p>
                </div>
                <div className="flex items-center justify-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-right">
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">
                    Destino
                  </p>
                  <p className="mt-1 text-lg font-bold text-slate-900 leading-tight">
                    {booking.destination}
                  </p>
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Data & Hora - Combined for Mobile Simplicity */}
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Calendar className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wide">
                      Data & Hora
                    </p>
                    <p className="text-sm font-bold text-slate-900">
                      {dayjs(booking.date).format("DD/MM/YYYY")}{" "}
                      <span className="text-slate-300">|</span>{" "}
                      {dayjs(booking.date).format("HH:mm")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Passengers */}
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Users className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wide">
                      {booking.passengersQty} Passageiros
                    </p>
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {passengersName.join(", ")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-400 tracking-wide">
                      Contato
                    </p>
                    <p className="text-sm font-bold text-slate-900">
                      {booking.phone}
                    </p>
                  </div>
                </div>
              </div>

              {/* Flight (if exists) */}
              {booking.flight && (
                <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Plane className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-slate-400 tracking-wide">
                        Voo
                      </p>
                      <p className="text-sm font-bold text-slate-900">
                        {booking.flight}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment */}
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm col-span-1 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <DollarSign className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-slate-400 tracking-wide">
                        Valor Total
                      </p>
                      <p className="text-lg font-bold text-slate-900">
                        {symbol} {Number(booking.value).toLocaleString("pt-BR")}
                      </p>
                    </div>
                  </div>
                  {booking.paidValue > 0 && (
                    <div className="text-right">
                      <div className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        <CheckCircle2 className="h-3 w-3" />
                        Pago: {symbol}{" "}
                        {Number(booking.paidValue).toLocaleString("pt-BR")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Separator */}
            <div className="my-8 relative">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-slate-200"></div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <Button
                  onClick={handleOpenCheckin}
                  className="h-12 w-full bg-slate-900 text-white hover:bg-slate-800 rounded-xl shadow-lg shadow-slate-900/10"
                >
                  Ver Instruções de Embarque
                </Button>
                <Button
                  onClick={() =>
                    window.open(whatsappUrl(), "_blank")
                  }
                  className="h-12 w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl shadow-lg shadow-primary/20"
                >
                  Contato WhatsApp
                </Button>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                {isAppleDevice() && (
                  <Button
                    onClick={createAppleCalendarEvent}
                    variant="outline"
                    className="h-11 border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Apple Calendar
                  </Button>
                )}
                <Button
                  onClick={createGoogleCalendarEvent}
                  variant="outline"
                  className="h-11 border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Google Calendar
                </Button>
                <Button
                  onClick={openGoogleMaps}
                  variant="outline"
                  className="h-11 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-primary rounded-xl"
                >
                  Google Maps
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-500">
          Desenvolvido por{" "}
          <a
            href="https://richardpompeo.com.br"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-slate-700 hover:text-slate-900"
          >
            Richard Pompeo
          </a>
        </p>
      </div>
    </div>
  );
}
